import { EditorState, type Extension } from '@codemirror/state';

/**
 * PSTUDIO-4415: rewrite the text of a paste that fills the whole editor.
 *
 * `transform` receives the pasted text and returns the text to insert instead,
 * or `null` / the same text to keep the paste as it was. It runs only for a
 * single `input.paste` change whose range spans every non-blank character of
 * the document, so a paste into the middle of existing text, and typing, never
 * reach it. The caret ends after the inserted text.
 *
 * It lives in the design system for the same reason as `createKeymapExtension`:
 * an app must not import `@codemirror/*` itself, because two copies of
 * `@codemirror/state` in one bundle break CodeMirror at runtime.
 */
export function createPasteTransformExtension(
  transform: (pasted: string) => string | null
): Extension {
  return EditorState.transactionFilter.of((tr) => {
    if (!tr.docChanged || !tr.isUserEvent('input.paste')) return tr;

    let count = 0;
    let range: { from: number; to: number; text: string } | null = null;
    // The five parameters are the signature `ChangeSet.iterChanges` calls with.
    // eslint-disable-next-line max-params
    tr.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
      count += 1;
      range = { from: fromA, to: toA, text: inserted.toString() };
    });
    if (count !== 1 || range === null) return tr;
    const { from, to, text } = range as {
      from: number;
      to: number;
      text: string;
    };

    const doc = tr.startState.doc.toString();
    const coversAll =
      doc.slice(0, from).trim() === '' && doc.slice(to).trim() === '';
    if (!coversAll) return tr;

    const next = transform(text);
    if (next == null || next === text) return tr;

    return {
      changes: { from: 0, to: doc.length, insert: next },
      selection: { anchor: next.length },
      userEvent: 'input.paste',
      scrollIntoView: true,
    };
  });
}
